import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Loader2, CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { EditableDataGrid, GridColumn, ProductSearchResult } from '@/app/pages/sales-invoices/components/EditableDataGrid';
import { CreditNoteFormValues, creditNoteSchema } from './schema';
import { Site } from '@/app/types/site';
import { Item } from '@/app/types/item';
import { VoucherResponse, VoucherItemResponse } from '@/app/types/voucher';
import { Godown } from '@/app/types/inventory';
import { cn } from '@/lib/utils';
import { Decimal } from 'decimal.js';
import { getItemPriceDetails, PricingContext, ItemPriceDetails } from '@/app/lib/pricingUtils';
import { usePreferenceStore } from '@/app/store/preferenceStore';
import { PartySelector } from '@/app/components/common/PartySelector';

// Define CalculationItem type at component level
type CalculationItem = { 
    quantity?: number | string | null, 
    rate?: number | string | null, 
    discount_percent?: number | string | null 
};

interface CreditNoteFormProps {
  creditNote?: Partial<VoucherResponse>; // For edit mode or pre-filled data
  sites: Site[];
  godowns: Godown[];
  voucherTypeId: number; // Credit Note voucher type ID (pre-fetched)
  isSubmitting: boolean;
  onSubmit: (data: CreditNoteFormValues) => void;
  searchItems: (query: string) => Promise<Item[]>;
  isLoadingItems: boolean;
  originalInvoiceItems?: VoucherItemResponse[]; // Original sales invoice items for validation
  originalInvoiceParty?: any; // Original invoice party details
  isDisabled?: boolean;
}

export const CreditNoteForm: React.FC<CreditNoteFormProps> = ({
  creditNote,
  sites,
  godowns,
  voucherTypeId,
  isSubmitting,
  onSubmit,
  searchItems,
  isLoadingItems,
  originalInvoiceItems = [],
  originalInvoiceParty,
  isDisabled = false,
}) => {
  const isEditMode = !!creditNote?.id; 
  const gridRef = useRef<HTMLDivElement>(null);
  
  // Get user preferences
  const { defaultSiteId, defaultGodownId: preferenceGodownId } = usePreferenceStore();
  
  // Default warehouse for all items
  const [defaultGodownId, setDefaultGodownId] = useState<number>(
    godowns.length > 0 ? (
      // Use preference godown if available, otherwise first godown
      preferenceGodownId && godowns.some(g => g.id === preferenceGodownId) 
        ? preferenceGodownId 
        : godowns[0].id
    ) : 0
  );
  
  // Prepare default values
  const defaultValues: Partial<CreditNoteFormValues> = {
    voucher_type_id: voucherTypeId,
    // Use preference site if available
    site_id: defaultSiteId && sites.some(s => s.id === defaultSiteId) ? defaultSiteId : undefined,
    transaction_date: new Date(),
    total_amount: 0,
    items: [],
  };

  // Initialize form first to avoid reference errors
  const form = useForm<CreditNoteFormValues>({
    resolver: zodResolver(creditNoteSchema),
    defaultValues: creditNote?.id
      ? {
          ...defaultValues,
          voucher_type_id: creditNote.voucher_type_id || voucherTypeId,
          party_id: creditNote.party_id || undefined,
          site_id: creditNote.site_id || undefined,
          transaction_date: creditNote.transaction_date
            ? new Date(creditNote.transaction_date)
            : new Date(),
          reference_number: creditNote.reference_number || '',
          reference_invoice_number: (creditNote as any).reference_invoice_number || '',
          narration: creditNote.narration || '',
          linked_voucher_id: creditNote.linked_voucher_id || undefined,
          total_amount: parseFloat(String(creditNote.total_amount || 0)),
          items: [],
        }
      : {
          ...defaultValues,
          // Explicitly set the original invoice's party_id and site_id when creating from invoice
          party_id: originalInvoiceParty?.id || undefined,
          site_id: originalInvoiceItems.length > 0 && creditNote?.site_id ? creditNote.site_id : defaultValues.site_id,
          reference_invoice_number: originalInvoiceItems.length > 0 ? creditNote?.voucher_number : undefined,
        },
  });

  // Initialize items state for the grid
  const [lineItems, setLineItems] = useState<Record<string, unknown>[]>([]);
  
  // Map to track original invoice item quantities
  const [originalQuantities, setOriginalQuantities] = useState<Map<number, number>>(new Map());

  // Initialize the line items for the grid AND update form state
  useEffect(() => {
    // Keep track of original invoice item quantities for validation
    if (originalInvoiceItems && originalInvoiceItems.length > 0) {
      const quantityMap = new Map<number, number>();
      originalInvoiceItems.forEach(item => {
        if (item.id && item.quantity) {
          quantityMap.set(item.id, parseFloat(String(item.quantity)));
        }
      });
      setOriginalQuantities(quantityMap);
    }

    const apiItems = creditNote?.items || [];
    console.log('[CreditNoteForm] API items to process:', apiItems);
    
    let itemsForGrid: Record<string, unknown>[] = [];
    let itemsForForm: CreditNoteFormValues['items'] = [];
    let calculatedTotal = 0;

    // Define calculation helpers
    const calculateDiscountAmount = (item: CalculationItem): number => {
        const quantity = parseFloat(String(item.quantity || 0));
        const rate = parseFloat(String(item.rate || 0));
        const discountPercent = parseFloat(String(item.discount_percent || 0));
        const grossAmount = quantity * rate;
        const discountAmount = (grossAmount * discountPercent) / 100;
        return Math.round(discountAmount * 100) / 100;
    };

    const calculateFinalAmount = (item: CalculationItem): number => {
        const quantity = parseFloat(String(item.quantity || 0));
        const rate = parseFloat(String(item.rate || 0));
        const discountAmount = calculateDiscountAmount(item);
        const grossAmount = quantity * rate;
        return Math.round((grossAmount - discountAmount) * 100) / 100;
    };

    if (apiItems && apiItems.length > 0) {
      try {
        itemsForGrid = apiItems.map((item) => {
          const quantity = parseFloat(String(item.quantity || 0));
          const rate = parseFloat(String(item.rate || 0));
          const discountPercent = parseFloat(String(item.discount_percent || 0));
          
          // Use API-provided values for tax and totals if available
          const discountAmount = item.discount_amount ? parseFloat(String(item.discount_amount)) : calculateDiscountAmount({ quantity, rate, discount_percent: discountPercent });
          const taxableAmount = item.taxable_amount ? parseFloat(String(item.taxable_amount)) : calculateFinalAmount({ quantity, rate, discount_percent: discountPercent });
          const taxAmount = item.tax_amount ? parseFloat(String(item.tax_amount)) : 0;
          const totalAmount = item.total_amount ? parseFloat(String(item.total_amount)) : (taxableAmount + taxAmount);
          
          return {
            id: item.id,
            original_invoice_item_id: item.original_invoice_item_id,
            item_id: item.item_id,
            item_name: item.description || `Item ID: ${item.item_id}`,
            item_code: '',
            description: item.description || '',
            quantity: quantity,
            unit_id: item.unit_id,
            rate: rate,
            discount_percent: discountPercent,
            discount_amount: discountAmount,
            tax_rate_id: item.tax_rate_id,
            taxable_amount: taxableAmount,
            tax_amount: taxAmount,
            amount: totalAmount,
            godown_id: item.godown_id || defaultGodownId,
          };
        });
        
        itemsForForm = itemsForGrid.map(item => ({
          id: typeof item.id === 'number' ? item.id : undefined,
          original_invoice_item_id: typeof item.original_invoice_item_id === 'number' ? item.original_invoice_item_id : undefined,
          item_id: typeof item.item_id === 'number' ? item.item_id : 0,
          quantity: parseFloat(String(item.quantity || 0)),
          rate: parseFloat(String(item.rate || 0)),
          unit_id: typeof item.unit_id === 'number' ? item.unit_id : 0,
          discount_percent: parseFloat(String(item.discount_percent || 0)), 
          tax_rate_id: typeof item.tax_rate_id === 'number' ? item.tax_rate_id : null,
          total_amount: parseFloat(String(item.amount || 0)),
          description: String(item.description || ''),
          godown_id: typeof item.godown_id === 'number' ? item.godown_id : defaultGodownId,
        }));

        calculatedTotal = itemsForForm.reduce((sum, item) => sum + (item.total_amount || 0), 0);

      } catch (error) {
        console.error('[CreditNoteForm] Error formatting items:', error);
        itemsForGrid = [{ item_id: '', quantity: 1, rate: 0, discount_percent: 0, discount_amount: 0, amount: 0, description: '', godown_id: defaultGodownId }];
        itemsForForm = [];
        calculatedTotal = 0;
      }
    } else {
      itemsForGrid = [{ item_id: '', quantity: 1, rate: 0, discount_percent: 0, discount_amount: 0, amount: 0, description: '', godown_id: defaultGodownId }];
      itemsForForm = []; 
      calculatedTotal = 0;
    }

    setLineItems(itemsForGrid);
    form.setValue('items', itemsForForm);
    form.setValue('total_amount', calculatedTotal);

  }, [creditNote, isEditMode, defaultGodownId, form, originalInvoiceItems]); 
  
  // Update line items when default godown changes
  useEffect(() => {
    if (lineItems.length > 0) {
      setLineItems(currentItems => currentItems.map(item => {
        if (!item.godown_id || !item.item_id) {
          return { ...item, godown_id: defaultGodownId };
        }
        return item;
      }));
    }
  }, [defaultGodownId]); 
  
  // Update form state when lineItems change
  useEffect(() => {
    // Skip initial run with placeholder data
    if (!creditNote && !isEditMode && lineItems.length === 1 && !lineItems[0].item_id) {
      console.log('[CreditNoteForm] Skipping initial placeholder row update for new credit note');
      return;
    }
    if (isEditMode && !creditNote?.items) {
      console.log('[CreditNoteForm] Skipping update in edit mode with no items');
      return;
    }
    
    console.log('[CreditNoteForm] Updating form state from lineItems:', lineItems);
    
    const filteredItems = lineItems.filter(item => {
      const hasItemId = item && (item.item_id || typeof item.item_id === 'number');
      return hasItemId;
    });
    
    console.log('[CreditNoteForm] Filtered items for form update:', filteredItems.length);
    
    // Update form items array for submission
    const formItems = filteredItems.map(item => {
      // Ensure all numeric values are properly parsed
      const quantity = parseFloat(String(item.quantity || 0));
      const rate = parseFloat(String(item.rate || 0));
      const discountPercent = parseFloat(String(item.discount_percent || 0));
      const amount = parseFloat(String(item.amount || 0));
      
      return {
        id: typeof item.id === 'number' ? item.id : undefined,
        original_invoice_item_id: typeof item.original_invoice_item_id === 'number' ? item.original_invoice_item_id : undefined,
        item_id: typeof item.item_id === 'number' ? item.item_id : 0,
        quantity: isNaN(quantity) ? 0 : quantity,
        unit_id: typeof item.unit_id === 'number' ? item.unit_id : 0,
        rate: isNaN(rate) ? 0 : rate,
        discount_percent: isNaN(discountPercent) ? 0 : discountPercent,
        tax_rate_id: typeof item.tax_rate_id === 'number' ? item.tax_rate_id : null,
        total_amount: isNaN(amount) ? 0 : amount,
        description: String(item.description || ''),
        godown_id: typeof item.godown_id === 'number' ? item.godown_id : 
                parseInt(String(item.godown_id || defaultGodownId), 10) || defaultGodownId,
      };
    });
        
    // Recalculate total amount
    const total = formItems.reduce((sum, item) => {
        return sum + (item.total_amount || 0); 
    }, 0);
    const roundedTotal = Math.round(total * 100) / 100;
    
    console.log('[CreditNoteForm] Setting form values - total:', roundedTotal, 'items count:', formItems.length);

    form.setValue('items', formItems, { shouldValidate: false });
    form.setValue('total_amount', roundedTotal, { shouldValidate: false }); 

  }, [lineItems, defaultGodownId, form, isEditMode, creditNote]); 

  // Update form values when original invoice data changes
  useEffect(() => {
    if (originalInvoiceParty && originalInvoiceParty.id && !isEditMode) {
      form.setValue('party_id', originalInvoiceParty.id);
      console.log('[CreditNoteForm] Setting party_id from originalInvoiceParty:', originalInvoiceParty.id);
    }
  }, [originalInvoiceParty, form, isEditMode]);

  // Make sure site_id is properly set when creating from original invoice
  useEffect(() => {
    if (originalInvoiceItems.length > 0 && !isEditMode) {
      // If we have site_id from creditNote props, use it
      const siteId = creditNote?.site_id;
      if (siteId) {
        form.setValue('site_id', siteId);
        console.log('[CreditNoteForm] Setting site_id from creditNote:', siteId);
      }
    }
  }, [originalInvoiceItems, creditNote, form, isEditMode]);

  // Ensure reference number is set properly
  useEffect(() => {
    if (originalInvoiceItems.length > 0 && !isEditMode && creditNote?.reference_number) {
      // Log received reference number
      console.log('[CreditNoteForm] Original reference_number received:', creditNote.reference_number);
      
      form.setValue('reference_number', creditNote.reference_number);
      console.log('[CreditNoteForm] Setting reference_number:', creditNote.reference_number);
    }
  }, [originalInvoiceItems, creditNote, form, isEditMode]);

  // Handle form submission
  const handleSubmit: SubmitHandler<CreditNoteFormValues> = (data) => {
    try {
      data.voucher_type_id = voucherTypeId;
      
      // Log form data to check if party_id is present and validate
      console.log('[CreditNoteForm] Form submission data:', {
        party_id: data.party_id,
        site_id: data.site_id,
        reference_invoice_number: data.reference_invoice_number,
        items_count: data.items?.length || 0
      });
      
      // Filter out items with missing product IDs or zero quantity
      const validItems = data.items.filter(item => 
        item.item_id && item.quantity > 0
      );
      
      if (validItems.length === 0) {
        toast.error("Cannot submit credit note with no valid items.");
        return;
      }
      
      // Validate if return quantities exceed original quantities
      for (const item of validItems) {
        if (item.original_invoice_item_id) {
          const originalQty = originalQuantities.get(item.original_invoice_item_id);
          if (originalQty !== undefined && item.quantity > originalQty) {
            toast.error(`Return quantity (${item.quantity}) cannot exceed original invoice quantity (${originalQty}) for ${item.description || 'an item'}.`);
            return;
          }
        }
      }
      
      const finalTotalAmount = validItems.reduce((sum, item) => sum + (item.total_amount || 0), 0);

      // Process each item to ensure required fields
      const processedItems = validItems.map(item => {
        // Create a new object without the extra field
        const { total_amount, ...itemWithoutAmount } = item;
        
        return {
          ...itemWithoutAmount,
          discount_percent: typeof item.discount_percent === 'number' ? item.discount_percent : 0,
        };
      });

      const formattedData = {
        ...data, 
        items: processedItems,
        transaction_date: format(data.transaction_date, 'yyyy-MM-dd'),
        total_amount: Math.round(finalTotalAmount * 100) / 100,
      };
      
      onSubmit(formattedData as CreditNoteFormValues);
    } catch (error) {
      toast.error(`Form submission error: ${(error as Error).message}`);
    }
  };

  // Wrapper for searchItems to convert Item[] to expected format
  const searchProductsWrapper = async (searchTerm: string): Promise<ProductSearchResult[]> => {
    if (!searchTerm || searchTerm.length < 2) return [];
    
    try {
      const items = await searchItems(searchTerm);
      return items.map(item => ({
        id: item.id,
        name: item.name || '',
        code: item.code || null, 
        description: item.description || item.name || null,
        tax_rate_id: item.tax_rate_id || null,
        unit_id: item.unit_id || null,
      }));
    } catch (error) {
      console.error('Error searching products:', error);
      return [];
    }
  };

  // Handle product selection
  const handleProductSelect = async (product: ProductSearchResult, rowIndex: number): Promise<void> => {
    console.log(`[CreditNoteForm] handleProductSelect for row ${rowIndex}, item ID: ${product.id}`);
    
    // Get the current quantity for the row (default to 1 if not set)
    const currentRow = lineItems[rowIndex] || {};
    const currentQuantity = new Decimal(String(currentRow.quantity || 1));
    const currentSiteId = form.watch('site_id');
    
    console.log(`[CreditNoteForm] Product selection context - quantity: ${currentQuantity}, siteId: ${currentSiteId}`);

    // Try to find this product in original invoice items
    const originalItem = originalInvoiceItems.find(item => item.item_id === product.id);
    let rate = 0;
    let discount_percent = 0;
    let tax_rate_id = null;
    let unit_id = typeof product.unit_id === 'number' ? product.unit_id : null;
    let original_invoice_item_id = undefined;
    
    if (originalItem) {
      // Use values from original invoice item
      rate = parseFloat(String(originalItem.rate || 0));
      discount_percent = parseFloat(String(originalItem.discount_percent || 0));
      tax_rate_id = originalItem.tax_rate_id;
      unit_id = originalItem.unit_id;
      original_invoice_item_id = originalItem.id;
    } else {
      // Use pricing utility if original item not found
      const pricingContext: PricingContext = {
        itemId: product.id,
        siteId: currentSiteId,
        quantity: currentQuantity
      };

      try {
        const priceDetails = getItemPriceDetails(pricingContext);
        rate = priceDetails ? priceDetails.rate.toNumber() : 0;
        discount_percent = priceDetails ? priceDetails.discount_percent.toNumber() : 0;
        tax_rate_id = priceDetails ? priceDetails.tax_rate_id : null;
      } catch (error) {
        console.error("[CreditNoteForm] Error fetching price details:", error);
        toast.error(`Error calculating price for ${product.name}: ${(error as Error).message}`);
      }
    }

    // Calculate amounts based on rate, quantity and discount
    const discountAmount = (currentQuantity.toNumber() * rate * discount_percent) / 100;
    const taxableAmount = currentQuantity.toNumber() * rate - discountAmount;
    // Tax amount would be calculated based on taxableAmount and tax_rate_id
    const taxAmount = 0; // Simplified - in real app this would use tax rate
    const finalAmount = taxableAmount + taxAmount;

    // Update the grid data state
    setLineItems(currentItems => {
      const newItems = [...currentItems];
      if (newItems[rowIndex]) {
        // Start with existing row data and basic product info
        const updatedRow = {
          ...newItems[rowIndex],
          item_id: product.id,
          item_name: product.name,
          item_code: product.code || '',
          description: product.description || product.name || '',
          godown_id: newItems[rowIndex].godown_id || defaultGodownId,
          quantity: currentQuantity.toNumber(),
          unit_id: unit_id,
          original_invoice_item_id: original_invoice_item_id,
          // Use values from original invoice or pricing utility
          rate: rate,
          discount_percent: discount_percent,
          discount_amount: discountAmount,
          taxable_amount: taxableAmount,
          tax_rate_id: tax_rate_id, 
          tax_amount: taxAmount,
          amount: finalAmount,
        };
        newItems[rowIndex] = updatedRow;
        console.log(`[CreditNoteForm] Updated row ${rowIndex} data:`, updatedRow);
      }
      return newItems;
    });
  };

  // Column definition with original quantity information
  const columns: GridColumn[] = useMemo(() => [
    { field: 'item_id', header: 'Product', type: 'product', width: '200px' },
    { 
      field: 'quantity', 
      header: 'Return Qty', 
      type: 'number', 
      width: '100px',
      // Add a custom tooltip with original quantity info when hovering
      tooltip: (row) => {
        const originalItemId = row.original_invoice_item_id;
        const originalQty = originalItemId ? originalQuantities.get(originalItemId) : null;
        return originalQty ? `Original Qty: ${originalQty}` : '';
      }
    },
    { field: 'rate', header: 'Rate', type: 'price', width: '100px' },
    { field: 'discount_percent', header: 'Disc %', type: 'number', width: '80px' },
    // Read-only calculated fields populated by the pricing util
    { field: 'taxable_amount', header: 'Taxable Amt', type: 'price', editable: false, width: '120px' },
    { field: 'tax_amount', header: 'Tax Amt', type: 'price', editable: false, width: '100px' },
    { field: 'amount', header: 'Amount', type: 'price', editable: false, width: '120px' },
    { field: 'description', header: 'Description', type: 'text', width: '180px' },
    { field: 'actions', header: '', type: 'actions', width: '50px' },
  ], [originalQuantities]); // Add dependency on originalQuantities

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* Party and Date Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Customer Selection - Disabled when creating from invoice */}
          <FormField
            control={form.control}
            name="party_id"
            render={({ field }) => {
              // Find party name from the original invoice if available
              let partyName = "Selected Customer";
              if (originalInvoiceItems.length > 0) {
                if (originalInvoiceParty?.name) {
                  partyName = originalInvoiceParty.name;
                } else if (creditNote?.party?.name) {
                  partyName = creditNote.party.name;
                } else {
                  // Try to find from the partyId
                  partyName = field.value ? `Customer ID: ${field.value}` : "Selected Customer";
                }
              }
              
              return (
                <FormItem>
                  <FormLabel>Customer *</FormLabel>
                  {originalInvoiceItems.length > 0 ? (
                    // Show a disabled Input with the customer name from the original invoice
                    // Use a hidden input to maintain form state connection
                    <>
                      <Input 
                        value={partyName}
                        disabled={true}
                      />
                      <input 
                        type="hidden"
                        {...field}
                        value={field.value || originalInvoiceParty?.id}
                      />
                    </>
                  ) : (
                    // Show the normal selector when not creating from invoice
                    <PartySelector
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      placeholder="Select or create a customer..."
                      filterPartyType="customer"
                      disabled={isSubmitting || isDisabled}
                    />
                  )}
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          {/* Site Selection - Disabled when creating from invoice */}
          <FormField
            control={form.control}
            name="site_id"
            render={({ field }) => {
              // Find site name
              const siteName = sites.find(site => site.id === field.value)?.name || "Default Site";
              
              return (
                <FormItem>
                  <FormLabel>Site</FormLabel>
                  {originalInvoiceItems.length > 0 ? (
                    // Show a disabled Input with the site name
                    <>
                      <Input 
                        value={siteName}
                        disabled={true}
                      />
                      <input 
                        type="hidden"
                        {...field}
                        value={field.value}
                      />
                    </>
                  ) : (
                    // Show the normal selector when not creating from invoice
                    <Select
                      onValueChange={(value) => field.onChange(parseInt(value))}
                      value={field.value?.toString()}
                      disabled={isSubmitting || isDisabled}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a site" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {sites.map((site) => (
                          <SelectItem key={site.id} value={site.id.toString()}>
                            {site.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          {/* Transaction Date */}
          <FormField
            control={form.control}
            name="transaction_date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Date *</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "w-full pl-3 text-left font-normal",
                          !field.value && "text-muted-foreground"
                        )}
                        disabled={isSubmitting || isDisabled}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Pick a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={field.onChange}
                      disabled={(date) => date > new Date() || isSubmitting || isDisabled}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Reference Number */}
          <FormField
            control={form.control}
            name="reference_number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Reference Number</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Invoice reference number..." 
                    {...field} 
                    disabled={isSubmitting || isDisabled || originalInvoiceItems.length > 0} 
                  />
                </FormControl>
                {originalInvoiceItems.length > 0 && (
                  <p className="text-xs text-muted-foreground">Using original invoice number as reference</p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Narration/Notes Field */}
        <FormField
          control={form.control}
          name="narration"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter notes about this credit note..."
                  className="resize-none"
                  {...field}
                  disabled={isSubmitting || isDisabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Items Grid */}
        <div>
          <Card>
            <CardContent className="p-0">
              <EditableDataGrid
                ref={gridRef}
                data={lineItems}
                setData={setLineItems}
                columns={columns}
                searchFunction={searchProductsWrapper}
                onProductSelect={handleProductSelect}
                isLoading={isLoadingItems}
                isDisabled={isSubmitting || isDisabled}
                godowns={godowns} // Pass for warehouse selection in grid
                defaultGodownId={defaultGodownId} // Default warehouse
                onDefaultGodownChange={setDefaultGodownId} // Set default warehouse
              />
            </CardContent>
          </Card>
        </div>

        {/* Total Amount & Submit Button */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
          <div className="text-xl font-semibold">
            Total: {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(form.watch('total_amount') || 0)}
          </div>
          <Button type="submit" disabled={isSubmitting || isDisabled}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Credit Note'
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}; 